import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  ApiResponse,
  TableFilterParams,
  ReportIssuePayload,
} from "interface/reportIssue";
import type { StatusInterface } from "interface/common";
import { PAGE_LIMIT } from "@utils/constant/common";

const getFormattedParams = (params: TableFilterParams = {}) => {
  const normalizeFilterValue = (value?: string | string[] | null): string => {
    if (Array.isArray(value)) return value[0] ?? "";
    return value ?? "";
  };

  const normalizeMultiValue = (value?: string[] | string | null): string => {
    if (value == null) return "";
    if (Array.isArray(value)) return value.length ? value.join("-") : "";
    if (typeof value === "string") return value.trim();
    return "";
  };

  const p = params as TableFilterParams & {
    sort?: string;
    direction?: "asc" | "desc";
    limit?: number;
  };

  const page = p.page ?? 1;
  const limit = p.pageSize ?? p.limit ?? PAGE_LIMIT;

  const formatted: Record<string, string | number> = {
    page,
    limit,
  };

  const title = normalizeFilterValue(p.title);
  const type = normalizeMultiValue(p.type);
  const status = normalizeMultiValue(p.status);
  const updatedAt = normalizeFilterValue(p.updatedAt);

  if (title) formatted.title = title;
  if (type) formatted.type = type;
  if (status) formatted.status = status;
  if (updatedAt) formatted.updatedAt = updatedAt;

  const sortField = p.sortField ?? p.sort;
  if (sortField) {
    formatted.sort = sortField;
    if (p.sortOrder === "ascend") formatted.direction = "asc";
    else if (p.sortOrder === "descend") formatted.direction = "desc";
    else if (p.direction === "asc" || p.direction === "desc") {
      formatted.direction = p.direction;
    }
  }

  return formatted;
};

export const reportIssueApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getReportIssues: builder.query<ApiResponse, TableFilterParams>({
      query: (params) => ({
        url: API.GET_REPORT_ISSUE,
        method: "GET",
        params: getFormattedParams(params),
      }),
      providesTags: ["ReportIssue"],
    }),
    createReportIssue: builder.mutation<ApiResponse, ReportIssuePayload>({
      query: ({ params }) => ({
        url: API.GET_REPORT_ISSUE,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["ReportIssue"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create report issue:", error);
        }
      },
    }),
    updateReportIssue: builder.mutation<ApiResponse, ReportIssuePayload>({
      query: ({ params }) => ({
        url: API.GET_REPORT_ISSUE,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: ["ReportIssue"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to update report issue:", error);
        }
      },
    }),
    updateReportIssueStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...body }) => ({
        url: `/${API.GET_REPORT_ISSUE}/status/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ReportIssue"],
    }),
    deleteReportIssue: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_REPORT_ISSUE}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ReportIssue"],
    }),
    bulkUpdateReportIssueStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_REPORT_ISSUE}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["ReportIssue"],
    }),
    bulkDeleteReportIssues: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_REPORT_ISSUE}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["ReportIssue"],
    }),
    reorderReportIssues: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_REPORT_ISSUE}/reorder`,
        method: "PUT",
        body: { ids },
      }),
      invalidatesTags: ["ReportIssue"],
    }),
  }),
});

export const {
  useGetReportIssuesQuery,
  useCreateReportIssueMutation,
  useUpdateReportIssueMutation,
  useUpdateReportIssueStatusMutation,
  useDeleteReportIssueMutation,
  useBulkUpdateReportIssueStatusMutation,
  useBulkDeleteReportIssuesMutation,
  useReorderReportIssuesMutation,
} = reportIssueApi;
